import React, {useEffect, useRef, useState} from 'react'
import {VStack, Box, Link, Text, useMultiStyleConfig} from '@chakra-ui/react'
import {Picture, CtaButton} from '_uiComponents'
import {usePageDesigner, useIsElementInViewport, useAnalytics, useOpenDrawer, useViewport} from '_customHooks'
import {CLDImageAsset} from '_types'
import {useProduct} from '@salesforce/commerce-sdk-react'
import {getProductAnalyticsInfo, getProductImpressionAnalytics} from '_analytics/utils/analyticsUtils'
import {createRedirectUrl, PAGE_DESIGNER_MODULES, updateCloudinaryPDImage} from '_utils'

import AOS from 'aos'
import 'aos/dist/aos.css'

export interface WallMixedImageProps {
    cld_image_desktop?: CLDImageAsset
    cld_image_mobile?: CLDImageAsset
    cld_image_tablet?: CLDImageAsset
    title?: string
    text?: string
    text_align?: string
    h_align?: string
    v_align?: string
    ctaText?: string
    ctaStyle?: 'button' | 'text' | 'text-cta' | 'outline' | 'blank'
    animation_enabled?: boolean
    animation_cta_text?: boolean
    animation_text?: boolean
    animation_duration?: string
    image_padding?: string
    button_background?: string
    layout_text_below_image: boolean
    content_color?: string
    button_color?: string
    productID?: string
    position: number
    analyticsID?: string
    image_link?: string
    analyticsList?: string
    listingLayout?: string
    mobileSideBySide?: boolean
    analyticsName?: string
    imageZoomHover?: boolean
}

const WallMixedImage: React.FC<WallMixedImageProps> = ({
    cld_image_desktop,
    cld_image_mobile,
    cld_image_tablet,
    image_link,
    title,
    text,
    text_align,
    h_align,
    v_align,
    ctaText,
    ctaStyle,
    animation_enabled,
    animation_cta_text,
    animation_text,
    animation_duration,
    image_padding,
    button_background,
    layout_text_below_image,
    content_color,
    button_color,
    productID,
    position,
    analyticsID,
    analyticsList,
    listingLayout,
    mobileSideBySide,
    analyticsName,
    imageZoomHover,
}) => {
    const {analyticsEventPromoView, analyticsEventPromoClick, analyticsProductClick, analyticsProductImpression} = useAnalytics()
    const {handleShopTheImageClick} = useOpenDrawer()
    const {linkObject, globalImageTransformation, CloudinaryPageDesignerCNAME} = usePageDesigner()
    const {isMobile} = useViewport()
    const targetElement = useRef(null)
    const isVisible = useIsElementInViewport(targetElement)
    const [alreadySentPromoView, setAlreadySentPromoView] = useState(false)
    const [alreadySentProductImpression, setAlreadySentProductImpression] = useState(false)
    const {data: product} = useProduct(
        {
            parameters: {
                id: productID!,
            },
        },
        {
            enabled: !!productID,
        },
    )
    const [moduleLink, setModuleLink] = useState(image_link ? createRedirectUrl(image_link, linkObject.hostlink, linkObject.locale, linkObject.site) : '')

    useEffect(() => {
        if (product && product.slugUrl) {
            setModuleLink(createRedirectUrl(product.slugUrl, linkObject.hostlink, linkObject.locale, linkObject.site))
        }
    }, [product])

    // animation_enabled it's for image
    // animation_text animation for title
    // animation_cta_text animation for cta only if his style is text
    useEffect(() => {
        if (animation_enabled || animation_text || animation_cta_text) {
            AOS.init()
        }
    }, [])

    useEffect(() => {
        if (isVisible && !alreadySentPromoView) {
            const analyticsCheck = productID ? productID : analyticsID
            analyticsEventPromoView({
                id: analyticsCheck ? analyticsCheck : 'null',
                position: position,
                creative: PAGE_DESIGNER_MODULES.WALL_MIXED_IMAGE,
                name: analyticsName ? analyticsName : 'null',
            })
            setAlreadySentPromoView(true)
        }
    }, [isVisible])

    useEffect(() => {
        if (isVisible && !alreadySentProductImpression && product) {
            const {productCurrency} = getProductAnalyticsInfo(product)
            const analyticsProductObject = getProductImpressionAnalytics({product, analyticsList: analyticsList, idx: position - 1})

            analyticsProductImpression({
                currencyCode: productCurrency,
                template: listingLayout,
                impressions: analyticsProductObject,
            })

            setAlreadySentProductImpression(true)
        }
    }, [isVisible, product])

    const triggerAnalyticsEventProductClick = (): void => {
        if (product) {
            const {productCurrency} = getProductAnalyticsInfo(product)
            const analyticsProductObject = getProductImpressionAnalytics({product, analyticsList: analyticsList, idx: position - 1})

            analyticsProductClick({
                currencyCode: productCurrency,
                dimension25: listingLayout,
                actionField: {
                    list: analyticsList ? analyticsList : '',
                },
                product: analyticsProductObject,
            })
        }
    }

    const triggerAnalyticsEventPromoClick = (textBtn: string, positionBtn?: string): void => {
        const analyticsCheck = productID ? productID : analyticsID
        analyticsEventPromoClick({
            id: analyticsCheck ? analyticsCheck : 'null',
            position: positionBtn ? String(position) + '-' + positionBtn : String(position),
            creative: PAGE_DESIGNER_MODULES.WALL_MIXED_IMAGE,
            promoText: textBtn,
            name: analyticsName ? analyticsName : 'null',
        })
    }

    const getPaddingValues = (image_padding?: string) => {
        if (!image_padding) return {top: 0, right: 0, bottom: 0, left: 0}
        const parts = image_padding.split(' ').map((v) => parseInt(v.replace('px', ''), 10) || 0)
        if (parts.length === 1) {
            return {top: parts[0], right: parts[0], bottom: parts[0], left: parts[0]}
        }
        if (parts.length === 2) {
            return {top: parts[0], right: parts[1], bottom: parts[0], left: parts[1]}
        }
        return {
            top: parts[0],
            right: parts[1],
            bottom: parts[2] || parts[0],
            left: parts[3] || parts[1],
        }
    }

    const imagePadding = getPaddingValues(image_padding)
    const styles = useMultiStyleConfig('WallMixedImage', {
        text_align,
        h_align,
        v_align,
        imagePadding,
        button_background,
        content_color,
        button_color,
        layout_text_below_image,
        mobileSideBySide,
        imageZoomHover,
    }) as Record<string, any>
    const isModuleClickable = !!moduleLink
    const resizedImgMobile = cld_image_mobile ? updateCloudinaryPDImage(cld_image_mobile, globalImageTransformation, CloudinaryPageDesignerCNAME) : ''
    const resizedImgTablet = cld_image_tablet ? updateCloudinaryPDImage(cld_image_tablet, globalImageTransformation, CloudinaryPageDesignerCNAME) : ''
    const resizedImgDesktop = cld_image_desktop ? updateCloudinaryPDImage(cld_image_desktop, globalImageTransformation, CloudinaryPageDesignerCNAME) : ''
    const resizedImgXlDesktop = cld_image_desktop ? updateCloudinaryPDImage(cld_image_desktop, globalImageTransformation, CloudinaryPageDesignerCNAME) : ''
    const ctaVariantMap = {
        button: 'button',
        outline: 'outline',
        text: 'textBorderTop',
        'text-cta': 'text',
    } as const
    const ctaVariant = ctaStyle && ctaStyle in ctaVariantMap ? ctaVariantMap[ctaStyle] : undefined

    const getCtaColor = (): string => {
        if (layout_text_below_image) {
            return ctaStyle === 'button' ? 'color.text.white' : 'color.text.black'
        } else {
            return 'color.text.white'
        }
    }

    const getCtaSize = () => {
        if (isMobile && mobileSideBySide) {
            return 'xs'
        }
        return 'md'
    }
    const TitleBlock = (
        <VStack {...(layout_text_below_image ? styles.titleContainerBelow : styles.titleContainer)}>
            {title && (
                <Text
                    as="h2"
                    {...(layout_text_below_image ? styles.titleBelow : styles.title)}
                    {...(animation_text
                        ? {
                              'data-aos': 'fade-left',
                              'data-aos-duration': animation_duration ? animation_duration : '1200',
                          }
                        : {})}
                >
                    {title}
                </Text>
            )}
            {text && <Text {...(layout_text_below_image ? styles.textBelow : styles.text)}>{text}</Text>}
            {ctaStyle !== 'blank' && ctaText && (
                <Box>
                    {/* empty wrapper fixes multi-line CTA text underline */}
                    <CtaButton
                        variant={ctaVariant}
                        href={moduleLink}
                        ctaColor={getCtaColor()}
                        background="color.text.black"
                        size={getCtaSize()}
                        onClick={(e) => {
                            if (layout_text_below_image) {
                                if (handleShopTheImageClick(e, image_link)) return
                                if (productID && product) triggerAnalyticsEventProductClick()
                                triggerAnalyticsEventPromoClick('')
                            }
                        }}
                        {...(animation_text
                            ? {
                                  'data-aos': 'animationCtaText',
                                  'data-aos-offset': '0',
                                  'data-aos-once': 'false',
                              }
                            : {})}
                    >
                        {ctaText}
                    </CtaButton>
                </Box>
            )}
        </VStack>
    )

    // Always render the link to keep SSR/CSR markup consistent.
    const WrapperLink: React.FC<{children: React.ReactNode}> = ({children}) => (
        <Link
            data-testid="wall-mixed-image-full-link"
            className="cc-link-full"
            href={moduleLink || undefined}
            title={''}
            aria-label={'wall banner link'}
            data-disabled={!isModuleClickable}
            {...styles.link}
            {...(!layout_text_below_image && imageZoomHover
                ? {
                      sx: {
                          '& img': {transition: 'transform 0.4s ease'},
                          '&:hover img': {transform: 'scale(1.05)'},
                      },
                  }
                : {})}
            onClick={(e) => {
                if (!isModuleClickable) {
                    e.preventDefault()
                    return
                }
                if (handleShopTheImageClick(e, image_link)) return
                if (productID && product) triggerAnalyticsEventProductClick()
                triggerAnalyticsEventPromoClick('')
            }}
        >
            {children}
        </Link>
    )

    return (
        <VStack {...styles.imageWrapper} data-testid="wall-mixed-image-wrapper" ref={targetElement}>
            <WrapperLink>
                <Box
                    {...styles.wrapperOverlay}
                    {...(layout_text_below_image && imageZoomHover
                        ? {
                              sx: {
                                  '& img': {transition: 'transform 0.4s ease'},
                                  '&:hover img': {transform: 'scale(1.05)'},
                              },
                          }
                        : {})}
                >
                    <Picture
                        overflow="hidden"
                        objectFit="cover"
                        imagexs={resizedImgMobile}
                        imagesm={resizedImgTablet}
                        imagelg={resizedImgDesktop}
                        imagexl={resizedImgXlDesktop}
                        alt={'#'}
                        {...(animation_enabled
                            ? {
                                  'data-aos': 'zoom-out',
                                  'data-aos-duration': animation_duration ? animation_duration : '1200',
                              }
                            : {})}
                    />
                    <Box {...styles.overlay}></Box>
                </Box>

                {!layout_text_below_image && <>{TitleBlock}</>}
            </WrapperLink>
            {layout_text_below_image && <>{TitleBlock}</>}
        </VStack>
    )
}

const MemoizedWallMixedImage = React.memo(WallMixedImage)

export {MemoizedWallMixedImage as WallMixedImage}
